import React, { useEffect, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { CContainer, CCard, CCardBody, CCardHeader, CFormInput, CFormSelect, CFormCheck, CRow, CCol } from "@coreui/react";
import settingsStore from "../../store/SettingsStore";
import periodPrefsStore from "../../store/PeriodPrefsStore";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import { SkeletonTable } from "../UI/Skeleton/Skeleton";
import CloudStatusState from "../UI/Status/CloudStatusState";
import {
  getStockBalances,
  getStockDepletion,
  getStockAnomalies,
  getPurchaseRiskList,
  getInventoryAudit,
} from "../../api/reportsApi";
import {
  StockBalancesView,
  StockDepletionView,
  StockAnomaliesView,
  PurchaseRiskListView,
  InventoryAuditView,
} from "../Assistant/ToolResultView";
import WarehouseInsightPanel from "../Insights/WarehouseInsightPanel";
import { logReportView } from "../../api/reportLogApi";
import { formatYmd } from "../../utils/dateRange";

const INVENTORY_AUDIT_MAX_DAYS = 92;

const WarehousePage = observer(() => {
  const [selectedServer, setSelectedServer] = useState(periodPrefsStore.selectedServer);
  const [balances, setBalances] = useState(null);
  const [balancesError, setBalancesError] = useState(null);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [depletion, setDepletion] = useState(null);
  const [depletionError, setDepletionError] = useState(null);
  const [depletionLoading, setDepletionLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [sortMode, setSortMode] = useState("name");
  const [onlyNegative, setOnlyNegative] = useState(false);

  const [anomalies, setAnomalies] = useState(null);
  const [anomaliesError, setAnomaliesError] = useState(null);
  const [anomaliesLoading, setAnomaliesLoading] = useState(false);
  const [purchaseRisk, setPurchaseRisk] = useState(null);
  const [purchaseRiskError, setPurchaseRiskError] = useState(null);
  const [purchaseRiskLoading, setPurchaseRiskLoading] = useState(false);

  const [auditFrom, setAuditFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - (INVENTORY_AUDIT_MAX_DAYS - 1));
    return formatYmd(d);
  });
  const [auditTo, setAuditTo] = useState(() => formatYmd(new Date()));
  const [auditQuickRange, setAuditQuickRange] = useState("");
  const [audit, setAudit] = useState(null);
  const [auditError, setAuditError] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadBalances = useCallback(() => {
    if (!selectedServer) return;
    setBalancesLoading(true);
    setBalancesError(null);
    getStockBalances(selectedServer)
      .then(setBalances)
      .catch((e) => setBalancesError(e?.response?.data?.message || e?.message || "Не удалось получить остатки."))
      .finally(() => setBalancesLoading(false));
  }, [selectedServer]);

  const loadDepletion = useCallback(() => {
    if (!selectedServer) return;
    setDepletionLoading(true);
    setDepletionError(null);
    getStockDepletion(selectedServer)
      .then((data) => (data.ok === false ? setDepletionError(data.error) : setDepletion(data)))
      .catch((e) => setDepletionError(e?.response?.data?.message || e?.message || "Не удалось получить прогноз."))
      .finally(() => setDepletionLoading(false));
  }, [selectedServer]);

  const loadAnomalies = useCallback(() => {
    if (!selectedServer) return;
    setAnomaliesLoading(true);
    setAnomaliesError(null);
    getStockAnomalies(selectedServer)
      .then((data) => (data.ok === false ? setAnomaliesError(data.error) : setAnomalies(data)))
      .catch((e) => setAnomaliesError(e?.response?.data?.message || e?.message || "Не удалось получить аномалии."))
      .finally(() => setAnomaliesLoading(false));
  }, [selectedServer]);

  const loadPurchaseRisk = useCallback(() => {
    if (!selectedServer) return;
    setPurchaseRiskLoading(true);
    setPurchaseRiskError(null);
    getPurchaseRiskList(selectedServer)
      .then((data) => (data.ok === false ? setPurchaseRiskError(data.error) : setPurchaseRisk(data)))
      .catch((e) => setPurchaseRiskError(e?.response?.data?.message || e?.message || "Не удалось получить список."))
      .finally(() => setPurchaseRiskLoading(false));
  }, [selectedServer]);

  const loadAudit = useCallback(
    (from, to) => {
      if (!selectedServer || !from || !to) return;
      setAuditLoading(true);
      setAuditError(null);
      getInventoryAudit(selectedServer, from, to)
        .then((data) => (data.ok === false ? setAuditError(data.error) : setAudit(data)))
        .catch((e) => setAuditError(e?.response?.data?.message || e?.message || "Не удалось получить аудит."))
        .finally(() => setAuditLoading(false));
    },
    [selectedServer],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      logReportView({ event: "report_view", reportKey: "warehouse", reportTitle: "Склад", userId, path: "/warehouse" });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (selectedServer) periodPrefsStore.setServer(selectedServer);
  }, [selectedServer]);

  useEffect(() => {
    if (!selectedServer) return;
    setBalances(null);
    setDepletion(null);
    setAnomalies(null);
    setPurchaseRisk(null);
    setAudit(null);
    loadBalances();
    loadDepletion();
    loadAnomalies();
    loadPurchaseRisk();
    if (auditFrom && auditTo) loadAudit(auditFrom, auditTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServer, loadBalances, loadDepletion, loadAnomalies, loadPurchaseRisk]);

  const storeOptions = useMemo(() => {
    const names = new Set((balances?.items || []).map((it) => it.store).filter(Boolean));
    return [...names].sort((a, b) => a.localeCompare(b, "ru"));
  }, [balances]);

  // Сервер iiko не связан с onlyNegative/storeFilter после смены данных —
  // если в новом списке нет выбранного склада, сбрасываем фильтр, а не
  // молча показываем пустую таблицу.
  useEffect(() => {
    if (storeFilter && storeOptions.length && !storeOptions.includes(storeFilter)) {
      setStoreFilter("");
    }
  }, [storeOptions, storeFilter]);

  const filteredBalances = useMemo(() => {
    if (!balances) return null;
    const q = filter.trim().toLowerCase();
    let items = balances.items || [];
    if (q) items = items.filter((it) => it.product?.toLowerCase().includes(q));
    if (storeFilter) items = items.filter((it) => it.store === storeFilter);
    if (onlyNegative) items = items.filter((it) => it.quantity < 0);

    items = [...items].sort((a, b) => {
      if (sortMode === "qty_asc") return a.quantity - b.quantity;
      if (sortMode === "qty_desc") return b.quantity - a.quantity;
      if (sortMode === "cost_desc") return (b.cost_sum ?? -Infinity) - (a.cost_sum ?? -Infinity);
      return (a.product || "").localeCompare(b.product || "", "ru");
    });

    return { ...balances, items };
  }, [balances, filter, storeFilter, sortMode, onlyNegative]);

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Склад</h4>
      <p className="text-body-secondary small">
        Текущие остатки и прогноз, через сколько дней закончится каждый товар — через iikoCloud (нужно подключить API-ключ в
        настройках сервера).
      </p>
      <ServerSelect servers={settingsStore.servers} value={selectedServer} onChange={(e) => setSelectedServer(e.target.value)} />

      <WarehouseInsightPanel serverId={selectedServer} />

      <CRow className="g-3">
        <CCol xs={12}>
          <CCard>
            <CCardHeader>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <span className="fw-semibold">Остатки</span>
                {balances && (
                  <span className="small text-body-secondary">
                    Показано {filteredBalances?.items?.length ?? 0} из {balances.items?.length ?? 0}
                  </span>
                )}
              </div>
              {balances && (
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <CFormInput
                    size="sm"
                    style={{ maxWidth: 220 }}
                    placeholder="Поиск по товару…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                  {storeOptions.length > 1 && (
                    <CFormSelect
                      size="sm"
                      style={{ maxWidth: 240 }}
                      value={storeFilter}
                      onChange={(e) => setStoreFilter(e.target.value)}
                    >
                      <option value="">Все склады</option>
                      {storeOptions.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </CFormSelect>
                  )}
                  <CFormSelect
                    size="sm"
                    style={{ maxWidth: 200 }}
                    value={sortMode}
                    onChange={(e) => setSortMode(e.target.value)}
                  >
                    <option value="name">Сортировка: по названию</option>
                    <option value="qty_asc">Сначала минусовые</option>
                    <option value="qty_desc">По остатку (убыв.)</option>
                    <option value="cost_desc">По сумме (убыв.)</option>
                  </CFormSelect>
                  <CFormCheck
                    id="warehouse-only-negative"
                    label="Только минусовые"
                    checked={onlyNegative}
                    onChange={(e) => setOnlyNegative(e.target.checked)}
                  />
                </div>
              )}
            </CCardHeader>
            <CCardBody style={{ maxHeight: 560, overflowY: "auto" }}>
              {balancesLoading && !balances && <SkeletonTable rows={6} />}
              {balancesError && <CloudStatusState message={balancesError} onRetry={loadBalances} />}
              {!balancesError && filteredBalances && filteredBalances.items.length === 0 && balances && (
                <div className="text-body-secondary small">Ничего не найдено по заданным фильтрам.</div>
              )}
              {!balancesError && filteredBalances && filteredBalances.items.length > 0 && (
                <StockBalancesView result={filteredBalances} />
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol xs={12}>
          <CCard>
            <CCardHeader className="fw-semibold">Прогноз списания (14 дней расхода)</CCardHeader>
            <CCardBody style={{ maxHeight: 480, overflowY: "auto" }}>
              {depletionLoading && !depletion && <SkeletonTable rows={6} />}
              {depletionError && <CloudStatusState message={depletionError} onRetry={loadDepletion} />}
              {!depletionError && depletion && <StockDepletionView result={depletion} />}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol xs={12} md={6}>
          <CCard className="h-100">
            <CCardHeader className="fw-semibold">Аномалии остатков</CCardHeader>
            <CCardBody style={{ maxHeight: 480, overflowY: "auto" }}>
              {anomaliesLoading && !anomalies && <SkeletonTable rows={6} />}
              {anomaliesError && <CloudStatusState message={anomaliesError} onRetry={loadAnomalies} />}
              {!anomaliesError && anomalies && <StockAnomaliesView result={anomalies} />}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol xs={12} md={6}>
          <CCard className="h-100">
            <CCardHeader className="fw-semibold">Риски закупки</CCardHeader>
            <CCardBody style={{ maxHeight: 480, overflowY: "auto" }}>
              {purchaseRiskLoading && !purchaseRisk && <SkeletonTable rows={6} />}
              {purchaseRiskError && <CloudStatusState message={purchaseRiskError} onRetry={loadPurchaseRisk} />}
              {!purchaseRiskError && purchaseRisk && <PurchaseRiskListView result={purchaseRisk} />}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol xs={12}>
          <CCard>
            <CCardHeader className="fw-semibold">Аудит инвентаризаций</CCardHeader>
            <CCardBody>
              <p className="text-body-secondary small">
                Излишки и недостачи по результатам проведённых инвентаризаций склада. Инвентаризации проходят
                нечасто, поэтому здесь можно смотреть за период до {INVENTORY_AUDIT_MAX_DAYS} дней — это не связано
                с обычным 31-дневным ограничением iiko в остальных отчётах, узкий запрос по одному типу проводки
                остаётся быстрым и на таком окне.
              </p>
              <PeriodFilters
                fromDate={auditFrom}
                toDate={auditTo}
                onFromChange={setAuditFrom}
                onToChange={setAuditTo}
                onRefresh={(from, to) => {
                  setAuditQuickRange("");
                  loadAudit(from, to);
                }}
                maxDays={INVENTORY_AUDIT_MAX_DAYS}
                showQuickRanges={false}
                quickRange={auditQuickRange}
                onQuickRangeChange={setAuditQuickRange}
                disabled={auditLoading}
                loading={auditLoading}
              />
              <div className="mt-3" style={{ maxHeight: 480, overflowY: "auto" }}>
                {auditLoading && !audit && <SkeletonTable rows={6} />}
                {auditError && <CloudStatusState message={auditError} onRetry={() => loadAudit(auditFrom, auditTo)} />}
                {!auditError && audit && <InventoryAuditView result={audit} />}
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
});

export default WarehousePage;
